import React, { useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box,
  Paper,
  Button,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Popover,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Divider,
  alpha,
  useTheme,
  Autocomplete,
} from '@mui/material';
import {
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  ViewColumn as ColumnsIcon,
  Inventory as InventoryIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import ReportHeader from '../../components/Reports/ReportHeader';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { inventoryReportsService, StockLevelDto } from '../../services/inventoryReports.service';
import { itemsService } from '../../services/items.service';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import apiClient from '../../services/api';
import config from '../../config';
import { fmtUnitPrice } from '../../utils/formatNumber';

const translations = {
  en: {
    title: 'Stock Levels Report',
    titleAr: 'تقرير أرصدة المخزون',
    filters: 'Filters',
    warehouse: 'Warehouse',
    branch: 'Branch',
    item: 'Item',
    allWarehouses: 'All Warehouses',
    allBranches: 'All Branches',
    allItems: 'All Items',
    showZeroQty: 'Show Zero Quantities',
    fromDate: 'From Date',
    toDate: 'To Date',
    refresh: 'Refresh',
    exportExcel: 'Export Excel',
    exportPdf: 'Export PDF',
    print: 'Print',
    columns: 'Columns',
    itemCode: 'Item Code',
    itemName: 'Item Name',
    unitName: 'Unit',
    location: 'Location',
    quantity: 'Quantity',
    reservedQty: 'Reserved',
    availableQty: 'Available',
    unitCost: 'Unit Cost',
    totalValue: 'Total Value',
    loading: 'Loading...',
    noData: 'No stock data found',
    rowsPerPage: 'Rows per page:',
    totalItems: 'Total Items',
    totalQuantity: 'Total Quantity',
    totalValueSummary: 'Total Value',
    subtotal: 'Subtotal',
    grandTotal: 'Grand Total',
    kwd: 'KWD'
  },
  ar: {
    title: 'Stock Levels Report',
    titleAr: 'تقرير أرصدة المخزون',
    filters: 'الفلاتر',
    warehouse: 'المخزن',
    branch: 'الفرع',
    item: 'الصنف',
    allWarehouses: 'كل المخازن',
    allBranches: 'كل الفروع',
    allItems: 'كل الأصناف',
    showZeroQty: 'إظهار الكميات الصفرية',
    fromDate: 'من تاريخ',
    toDate: 'إلى تاريخ',
    refresh: 'تحديث',
    exportExcel: 'تصدير Excel',
    exportPdf: 'تصدير PDF',
    print: 'طباعة',
    columns: 'الأعمدة',
    itemCode: 'كود الصنف',
    itemName: 'اسم الصنف',
    unitName: 'الوحدة',
    location: 'الموقع',
    quantity: 'الكمية',
    reservedQty: 'محجوز',
    availableQty: 'متاح',
    unitCost: 'سعر الوحدة',
    totalValue: 'القيمة الإجمالية',
    loading: 'جاري التحميل...',
    noData: 'لا توجد بيانات',
    rowsPerPage: 'عدد الصفوف:',
    totalItems: 'إجمالي الأصناف',
    totalQuantity: 'إجمالي الكمية',
    totalValueSummary: 'إجمالي القيمة',
    subtotal: 'المجموع الفرعي',
    grandTotal: 'الإجمالي العام',
    kwd: 'د.ك'
  }
};

interface Warehouse {
  warehouseId: number;
  warehouseName: string;
  warehouseNameAr: string;
}

interface Branch {
  branchId: number;
  branchName: string;
  branchNameAr: string;
}

interface Item {
  itemId: number;
  itemCode: string;
  itemName: string;
  itemNameAr: string;
}

const StockLevelsReport: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<StockLevelDto[]>([]);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  
  const [warehouseFilter, setWarehouseFilter] = useState<number | ''>('');
  const [branchFilter, setBranchFilter] = useState<number | ''>('');
  const [itemFilter, setItemFilter] = useState<number | ''>('');
  const [showZeroQty, setShowZeroQty] = useState(false);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);

  const [visibleColumns, setVisibleColumns] = useState({
    itemCode: true,
    itemName: true,
    unitName: true,
    location: true,
    quantity: true,
    reservedQty: true,
    availableQty: true,
    unitCost: true,
    totalValue: true
  });
  const [columnsAnchor, setColumnsAnchor] = useState<null | HTMLElement>(null);

  const [stats, setStats] = useState({
    totalItems: 0,
    totalQuantity: 0,
    totalValue: 0
  });

  useEffect(() => {
    loadCompanyInfo();
    loadDropdownData();
  }, []);

  useEffect(() => {
    loadData();
  }, [warehouseFilter, branchFilter, itemFilter, showZeroQty, fromDate, toDate, page, rowsPerPage]);

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
      
    } catch (error) {
      console.error('Failed to load company info:', error);
    }
  };

  const loadDropdownData = async () => {
    try {
      const warehousesResponse = await apiClient.get<Warehouse[]>('/Warehouses');
      setWarehouses(warehousesResponse.data);

      const branchesResponse = await apiClient.get<Branch[]>('/Branches');
      setBranches(branchesResponse.data);

      const itemsResponse = await itemsService.getAll();
      setItems(itemsResponse);
    } catch (error) {
      console.error('Failed to load dropdown data:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const result = await inventoryReportsService.getStockLevels({
        warehouseId: warehouseFilter || undefined,
        branchId: branchFilter || undefined,
        itemId: itemFilter || undefined,
        includeZeroQuantity: showZeroQty,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        pageNumber: page + 1,
        pageSize: rowsPerPage
      });

      setData(result.data);
      setTotalRecords(result.totalRecords);
      setStats({
        totalItems: result.totalRecords,
        totalQuantity: result.summary.totalQuantity,
        totalValue: result.summary.totalValue
      });
    } catch (error) {
      console.error('Failed to load stock levels:', error);
      toast.error(isRTL ? 'فشل تحميل البيانات' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getLocationName = (item: StockLevelDto): string => {
    if (item.warehouseName) {
      return isRTL ? (item.warehouseNameAr || item.warehouseName) : item.warehouseName;
    }
    if (item.branchName) {
      return isRTL ? (item.branchNameAr || item.branchName) : item.branchName;
    }
    return '-';
  };

  const getLocationNameEnglish = (item: StockLevelDto): string => {
    if (item.warehouseName) {
      return item.warehouseName;
    }
    if (item.branchName) {
      return item.branchName;
    }
    return '-';
  };

  const groupDataByLocation = () => {
    const grouped: { [key: string]: StockLevelDto[] } = {};
    data.forEach(item => {
      const location = getLocationName(item);
      if (!grouped[location]) grouped[location] = [];
      grouped[location].push(item);
    });
    return grouped;
  };

  const groupDataByLocationEnglish = () => {
    const grouped: { [key: string]: StockLevelDto[] } = {};
    data.forEach(item => {
      const location = getLocationNameEnglish(item);
      if (!grouped[location]) grouped[location] = [];
      grouped[location].push(item);
    });
    return grouped;
  };

  const handleColumnToggle = (column: keyof typeof visibleColumns) => {
    setVisibleColumns(prev => ({ ...prev, [column]: !prev[column] }));
  };

  const formatNumber = (num: number, decimals: number = 3) => {
    return num.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const getQuantityColor = (qty: number) => {
    if (qty === 0) return theme.palette.error.main;
    if (qty < 50) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(isRTL ? 'أرصدة المخزون' : 'Stock Levels');

      // Add Logo from Backend (base64)
      if (companyInfo?.logoBase64) {
        try {
          // Extract base64 data (remove data:image/png;base64, prefix)
          const base64Data = companyInfo.logoBase64.split(',')[1];
          
          const imageId = workbook.addImage({
            base64: base64Data,
            extension: 'png',
          });
          
          worksheet.addImage(imageId, {
            tl: { col: 0, row: 0 },
            ext: { width: 100, height: 80 }
          });
          
        } catch (err) {
          console.error('❌ Excel: Failed to add logo:', err);
        }
      } else {
        console.warn('⚠️ Excel: No logoBase64 available - skipping logo');
      }

      // Company Header - Row 1
      worksheet.mergeCells('C1:I1');
      const companyCell = worksheet.getCell('C1');
      companyCell.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
      companyCell.font = { size: 20, bold: true, color: { argb: 'FF1565C0' } };
      companyCell.alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getRow(1).height = 30;

      // Address - Row 2
      worksheet.mergeCells('C2:I2');
      const addressCell = worksheet.getCell('C2');
      addressCell.value = companyInfo?.address || '';
      addressCell.font = { size: 11, color: { argb: 'FF666666' } };
      addressCell.alignment = { horizontal: 'center' };

      // Contact - Row 3
      worksheet.mergeCells('C3:I3');
      const contactCell = worksheet.getCell('C3');
      contactCell.value = `${isRTL ? 'هاتف:' : 'Tel:'} ${companyInfo?.phone || ''} | ${isRTL ? 'بريد:' : 'Email:'} ${companyInfo?.email || ''}`;
      contactCell.font = { size: 10, color: { argb: 'FF666666' } };
      contactCell.alignment = { horizontal: 'center' };

      // Report Title - Row 5
      worksheet.mergeCells('A5:I5');
      const titleCell = worksheet.getCell('A5');
      titleCell.value = isRTL ? t.titleAr : t.title;
      titleCell.font = { size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = {
        type: 'gradient',
        gradient: 'angle',
        degree: 90,
        stops: [
          { position: 0, color: { argb: 'FF1565C0' } },
          { position: 1, color: { argb: 'FF1976D2' } }
        ]
      };
      worksheet.getRow(5).height = 35;

      // Date - Row 6
      worksheet.mergeCells('A6:I6');
      const dateCell = worksheet.getCell('A6');
      dateCell.value = `${isRTL ? 'التاريخ:' : 'Date:'} ${new Date().toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}`;
      dateCell.font = { size: 10, bold: true };
      dateCell.alignment = { horizontal: 'center' };
      dateCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE3F2FD' }
      };

      // Column Headers - Row 8
      const headers = [t.itemCode, t.itemName, t.unitName, t.location, t.quantity, t.reservedQty, t.availableQty, t.unitCost, t.totalValue];
      const headerRow = worksheet.getRow(8);
      headers.forEach((header, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.value = header;
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF1565C0' }
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = {
          top: { style: 'medium', color: { argb: 'FF1565C0' } },
          left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
          bottom: { style: 'medium', color: { argb: 'FF1565C0' } },
          right: { style: 'thin', color: { argb: 'FFFFFFFF' } }
        };
      });
      headerRow.height = 28;

      // Data with Grouping
      let currentRow = 9;
      const grouped = groupDataByLocation();
      
      Object.keys(grouped).forEach((location, groupIndex) => {
        const groupData = grouped[location];
        
        // Location Header
        const locationHeaderRow = currentRow;
        worksheet.mergeCells(`A${locationHeaderRow}:I${locationHeaderRow}`);
        const locationCell = worksheet.getCell(`A${locationHeaderRow}`);
        locationCell.value = `📍 ${location}`;
        locationCell.font = { bold: true, size: 12, color: { argb: 'FF1565C0' } };
        locationCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF5F5F5' }
        };
        locationCell.alignment = { horizontal: 'left', vertical: 'middle' };
        locationCell.border = {
          top: { style: 'medium', color: { argb: 'FF1565C0' } },
          left: { style: 'thin', color: { argb: 'FFD0D0D0' } },
          bottom: { style: 'thin', color: { argb: 'FFD0D0D0' } },
          right: { style: 'thin', color: { argb: 'FFD0D0D0' } }
        };
        worksheet.getRow(locationHeaderRow).height = 25;
        currentRow++;

        // Group Items
        let groupQty = 0;
        let groupValue = 0;

        groupData.forEach((item, itemIndex) => {
          const row = worksheet.getRow(currentRow);
          row.getCell(1).value = item.itemCode;
          row.getCell(2).value = isRTL ? item.itemNameAr : item.itemName;
          row.getCell(3).value = isRTL ? item.unitNameAr : item.unitName;
          row.getCell(4).value = location;
          row.getCell(5).value = item.quantity;
          row.getCell(6).value = item.reservedQuantity;
          row.getCell(7).value = item.availableQuantity;
          row.getCell(8).value = item.unitCost;
          row.getCell(9).value = item.totalValue;

          groupQty += item.quantity;
          groupValue += item.totalValue;

          // Styling
          row.alignment = { horizontal: 'center', vertical: 'middle' };
          row.height = 22;

          // Alternating colors within group
          const bgColor = itemIndex % 2 === 0 ? 'FFFFFFFF' : 'FFF9FAFB';
          row.eachCell((cell, colNumber) => {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: bgColor }
            };
            cell.border = {
              top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
              left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
              bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
              right: { style: 'thin', color: { argb: 'FFE0E0E0' } }
            };

            // Number formatting
            if (colNumber >= 5 && colNumber <= 9) {
              cell.numFmt = '#,##0.000';
              if (colNumber === 9) {
                cell.font = { bold: true, color: { argb: 'FF1565C0' } };
              }
            }
          });

          currentRow++;
        });

        // Subtotal Row
        const subtotalRow = worksheet.getRow(currentRow);
        subtotalRow.getCell(1).value = isRTL ? t.subtotal : t.subtotal;
        subtotalRow.getCell(2).value = '';
        subtotalRow.getCell(3).value = '';
        subtotalRow.getCell(4).value = '';
        subtotalRow.getCell(5).value = groupQty;
        subtotalRow.getCell(6).value = '';
        subtotalRow.getCell(7).value = '';
        subtotalRow.getCell(8).value = '';
        subtotalRow.getCell(9).value = groupValue;

        subtotalRow.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
        subtotalRow.alignment = { horizontal: 'center', vertical: 'middle' };
        subtotalRow.height = 25;

        // Apply styling only to used cells (1-9)
        for (let col = 1; col <= 9; col++) {
          const cell = subtotalRow.getCell(col);
          cell.fill = {
            type: 'gradient',
            gradient: 'angle',
            degree: 90,
            stops: [
              { position: 0, color: { argb: 'FF42A5F5' } },
              { position: 1, color: { argb: 'FF1976D2' } }
            ]
          };
          cell.border = {
            top: { style: 'medium', color: { argb: 'FF1565C0' } },
            left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
            bottom: { style: 'medium', color: { argb: 'FF1565C0' } },
            right: { style: 'thin', color: { argb: 'FFFFFFFF' } }
          };
          if (col >= 5) {
            cell.numFmt = '#,##0.000';
          }
        }

        currentRow++;
      });

      // Grand Total Row
      const grandTotalRow = worksheet.getRow(currentRow);
      grandTotalRow.getCell(1).value = isRTL ? `🏆 ${t.grandTotal}` : `🏆 ${t.grandTotal}`;
      grandTotalRow.getCell(2).value = '';
      grandTotalRow.getCell(3).value = '';
      grandTotalRow.getCell(4).value = '';
      grandTotalRow.getCell(5).value = stats.totalQuantity;
      grandTotalRow.getCell(6).value = '';
      grandTotalRow.getCell(7).value = '';
      grandTotalRow.getCell(8).value = '';
      grandTotalRow.getCell(9).value = stats.totalValue;

      grandTotalRow.font = { bold: true, size: 13, color: { argb: 'FFFFFFFF' } };
      grandTotalRow.alignment = { horizontal: 'center', vertical: 'middle' };
      grandTotalRow.height = 30;

      // Apply styling only to used cells (1-9)
      for (let col = 1; col <= 9; col++) {
        const cell = grandTotalRow.getCell(col);
        cell.fill = {
          type: 'gradient',
          gradient: 'angle',
          degree: 90,
          stops: [
            { position: 0, color: { argb: 'FF0D47A1' } },
            { position: 1, color: { argb: 'FF1565C0' } }
          ]
        };
        cell.border = {
          top: { style: 'double', color: { argb: 'FF0D47A1' } },
          left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
          bottom: { style: 'double', color: { argb: 'FF0D47A1' } },
          right: { style: 'thin', color: { argb: 'FFFFFFFF' } }
        };
        if (col >= 5) {
          cell.numFmt = '#,##0.000';
        }
      }

      // Column Widths
      worksheet.columns = [
        { width: 15 }, { width: 30 }, { width: 12 }, { width: 25 },
        { width: 14 }, { width: 14 }, { width: 14 }, { width: 14 }, { width: 16 }
      ];

      // Save
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `StockLevels_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();

      toast.success(isRTL ? 'تم التصدير بنجاح ✅' : 'Exported successfully ✅');
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error(isRTL ? 'فشل التصدير ❌' : 'Export failed ❌');
    }
  };

  const exportToPdf = async () => {
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;

      // Background
      doc.setFillColor(245, 250, 255);
      doc.rect(0, 0, pageWidth, 45, 'F');

      // Add Logo from Backend (base64)
      if (companyInfo?.logoBase64) {
        try {
          doc.addImage(companyInfo.logoBase64, 'PNG', 12, 8, 30, 25);
        } catch (err) {
          console.error('❌ PDF: Failed to add logo:', err);
        }
      } else {
        console.warn('⚠️ PDF: No logoBase64 available - skipping logo');
      }

      // Company Info
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(21, 101, 192);
      doc.text(
        companyInfo?.companyName || 'Company Name',
        pageWidth / 2, 
        15, 
        { align: 'center' }
      );

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(companyInfo?.address || '', pageWidth / 2, 22, { align: 'center' });
      doc.text(
        `Tel: ${companyInfo?.phone || ''} | Email: ${companyInfo?.email || ''}`,
        pageWidth / 2, 
        27, 
        { align: 'center' }
      );

      // Report Title
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.setFillColor(21, 101, 192);
      doc.roundedRect(pageWidth / 2 - 60, 32, 120, 10, 2, 2, 'F');
      doc.text(
        'Stock Levels Report',
        pageWidth / 2,
        38.5,
        { align: 'center' }
      );

      // Date
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(
        `Date: ${new Date().toLocaleDateString('en-GB')}`,
        pageWidth - 15,
        50,
        { align: 'right' }
      );

      // === TABLE SECTION ===
      const headers = [[
        'Item Code',
        'Item Name',
        'Unit',
        'Location',
        'Quantity',
        'Unit Cost',
        'Total Value'
      ]];

      const tableData: any[] = [];
      const grouped = groupDataByLocationEnglish(); // Use English grouping for PDF

      Object.keys(grouped).forEach((locationEnglish, groupIndex) => {
        const groupData = grouped[locationEnglish];

        // Location Header (English only to avoid encoding issues)
        tableData.push([{
          content: `${locationEnglish}`,
          colSpan: 7,
          styles: {
            fillColor: [25, 118, 210],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 10,
            halign: 'left',
            cellPadding: { top: 3, bottom: 3, left: 5, right: 5 }
          }
        }]);

        // Items
        let groupQty = 0;
        let groupValue = 0;

        groupData.forEach((item) => {
          tableData.push([
            item.itemCode,
            item.itemName, // Always English for PDF clarity
            item.unitName,
            locationEnglish,
            formatNumber(item.quantity),
            formatNumber(item.unitCost),
            formatNumber(item.totalValue)
          ]);

          groupQty += item.quantity;
          groupValue += item.totalValue;
        });

        // Subtotal Row
        tableData.push([
          {
            content: `Subtotal: ${locationEnglish}`,
            colSpan: 4,
            styles: {
              fillColor: [66, 165, 245],
              textColor: [255, 255, 255],
              fontStyle: 'bold',
              fontSize: 9,
              halign: 'center',
              cellPadding: { top: 2, bottom: 2, left: 3, right: 3 }
            }
          },
          {
            content: formatNumber(groupQty),
            styles: {
              fillColor: [66, 165, 245],
              textColor: [255, 255, 255],
              fontStyle: 'bold',
              halign: 'center'
            }
          },
          {
            content: '',
            styles: { fillColor: [66, 165, 245] }
          },
          {
            content: formatNumber(groupValue),
            styles: {
              fillColor: [66, 165, 245],
              textColor: [255, 255, 255],
              fontStyle: 'bold',
              halign: 'center'
            }
          }
        ]);
      });

      // Grand Total Row
      tableData.push([
        {
          content: 'GRAND TOTAL',
          colSpan: 4,
          styles: {
            fillColor: [13, 71, 161],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 12,
            halign: 'center',
            cellPadding: { top: 4, bottom: 4, left: 5, right: 5 }
          }
        },
        {
          content: formatNumber(stats.totalQuantity),
          styles: {
            fillColor: [13, 71, 161],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 11,
            halign: 'center'
          }
        },
        {
          content: '',
          styles: { fillColor: [13, 71, 161] }
        },
        {
          content: formatNumber(stats.totalValue),
          styles: {
            fillColor: [13, 71, 161],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 11,
            halign: 'center'
          }
        }
      ]);

      autoTable(doc, {
        head: headers,
        body: tableData,
        startY: 55,
        theme: 'striped',
        styles: {
          fontSize: 8,
          cellPadding: 2,
          halign: 'center',
          valign: 'middle'
        },
        headStyles: {
          fillColor: [21, 101, 192],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 9,
          halign: 'center'
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252]
        },
        columnStyles: {
          0: { cellWidth: 22, halign: 'center' },
          1: { cellWidth: 55, halign: 'left' },
          2: { cellWidth: 18, halign: 'center' },
          3: { cellWidth: 48, halign: 'center' },
          4: { cellWidth: 22, halign: 'right', fontStyle: 'bold', textColor: [21, 101, 192] },
          5: { cellWidth: 22, halign: 'right' },
          6: { cellWidth: 28, halign: 'right', fontStyle: 'bold', textColor: [21, 101, 192] }
        },
        margin: { left: 12, right: 12 }
      });

      // Footer
      const finalY = (doc as any).lastAutoTable?.finalY || 200;
      if (finalY < pageHeight - 15) {
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(7);
        doc.setTextColor(150, 150, 150);
        doc.text(
          `Generated: ${new Date().toLocaleString('en-GB')}`,
          pageWidth / 2,
          pageHeight - 8,
          { align: 'center' }
        );
      }

      doc.save(`StockLevels_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(isRTL ? 'تم التصدير بنجاح ✅' : 'Exported successfully ✅');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error(isRTL ? 'فشل التصدير ❌' : 'Export failed ❌');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const grouped = groupDataByLocation();

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Print Section */}
      <Box className="print-section">
        <ReportHeader
          reportTitle={t.title}
          reportTitleAr={t.titleAr}
          reportDate={new Date().toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}
        />
        
        <TableContainer sx={{ mt: 3 }}>
          <Table size="small" sx={{ 
            fontFamily: 'Arial, "Segoe UI", Tahoma, sans-serif',
            '& th, & td': { 
              border: '1px solid #1565C0',
              p: 1.5,
              fontSize: '12px',
              fontFamily: 'Arial, "Segoe UI", Tahoma, sans-serif',
              fontWeight: 500,
              color: '#000'
            },
            '& th': {
              fontWeight: 700
            }
          }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#1565C0' }}>
                {visibleColumns.itemCode && <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '13px', fontFamily: 'Arial, sans-serif' }}>{t.itemCode}</TableCell>}
                {visibleColumns.itemName && <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '13px', fontFamily: 'Arial, sans-serif' }}>{t.itemName}</TableCell>}
                {visibleColumns.unitName && <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '13px', fontFamily: 'Arial, sans-serif' }}>{t.unitName}</TableCell>}
                {visibleColumns.location && <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '13px', fontFamily: 'Arial, sans-serif' }}>{t.location}</TableCell>}
                {visibleColumns.quantity && <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '13px', fontFamily: 'Arial, sans-serif' }} align="right">{t.quantity}</TableCell>}
                {visibleColumns.reservedQty && <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '13px', fontFamily: 'Arial, sans-serif' }} align="right">{t.reservedQty}</TableCell>}
                {visibleColumns.availableQty && <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '13px', fontFamily: 'Arial, sans-serif' }} align="right">{t.availableQty}</TableCell>}
                {visibleColumns.unitCost && <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '13px', fontFamily: 'Arial, sans-serif' }} align="right">{t.unitCost}</TableCell>}
                {visibleColumns.totalValue && <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '13px', fontFamily: 'Arial, sans-serif' }} align="right">{t.totalValue}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.keys(grouped).map((location, groupIndex) => {
                const groupData = grouped[location];
                let groupQty = 0;
                let groupValue = 0;

                return (
                  <React.Fragment key={groupIndex}>
                    {/* Location Header */}
                    <TableRow>
                      <TableCell
                        colSpan={9}
                        sx={{
                          bgcolor: 'action.hover',
                          fontWeight: 'bold',
                          fontSize: '12px',
                          color: 'primary.main',
                          borderTop: '2px solid',
                          borderTopColor: 'primary.main'
                        }}
                      >
                        📍 {location}
                      </TableCell>
                    </TableRow>

                    {/* Items */}
                    {groupData.map((item, index) => {
                      groupQty += item.quantity;
                      groupValue += item.totalValue;
                      
                      return (
                        <TableRow key={index} sx={{ bgcolor: index % 2 === 0 ? 'background.paper' : 'action.hover' }}>
                          {visibleColumns.itemCode && <TableCell>{item.itemCode}</TableCell>}
                          {visibleColumns.itemName && <TableCell>{isRTL ? item.itemNameAr : item.itemName}</TableCell>}
                          {visibleColumns.unitName && <TableCell>{isRTL ? item.unitNameAr : item.unitName}</TableCell>}
                          {visibleColumns.location && <TableCell>{location}</TableCell>}
                          {visibleColumns.quantity && <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1565C0' }}>{formatNumber(item.quantity)}</TableCell>}
                          {visibleColumns.reservedQty && <TableCell align="right">{formatNumber(item.reservedQuantity)}</TableCell>}
                          {visibleColumns.availableQty && <TableCell align="right">{formatNumber(item.availableQuantity)}</TableCell>}
                          {visibleColumns.unitCost && <TableCell align="right">{fmtUnitPrice(item.unitCost)}</TableCell>}
                          {visibleColumns.totalValue && <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1565C0' }}>{formatNumber(item.totalValue)}</TableCell>}
                        </TableRow>
                      );
                    })}

                    {/* Subtotal */}
                    <TableRow sx={{ bgcolor: '#E3F2FD' }}>
                      <TableCell
                        colSpan={4}
                        sx={{ fontWeight: 'bold', color: '#1565C0', textAlign: 'center', fontSize: '11px' }}
                      >
                        {isRTL ? `المجموع الفرعي: ${location}` : `Subtotal: ${location}`}
                      </TableCell>
                      {visibleColumns.quantity && <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1565C0' }}>{formatNumber(groupQty)}</TableCell>}
                      <TableCell colSpan={3}></TableCell>
                      {visibleColumns.totalValue && <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1565C0' }}>{formatNumber(groupValue)}</TableCell>}
                    </TableRow>
                  </React.Fragment>
                );
              })}

              {/* Grand Total */}
              <TableRow sx={{ bgcolor: '#1565C0' }}>
                <TableCell
                  colSpan={4}
                  sx={{ fontWeight: 'bold', color: 'white', textAlign: 'center', fontSize: '13px' }}
                >
                  🏆 {t.grandTotal}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: 'white', fontSize: '12px' }}>{formatNumber(stats.totalQuantity)}</TableCell>
                <TableCell colSpan={3}></TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: 'white', fontSize: '12px' }}>{formatNumber(stats.totalValue)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Screen Section */}
      <Box className="no-print" sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), p: 1.5, borderRadius: 2, display: 'flex' }}>
            <InventoryIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight="bold" color="primary">
              {isRTL ? t.titleAr : t.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isRTL ? 'تقرير مفصل مع مجاميع فرعية لكل موقع' : 'Detailed report with subtotals per location'}
            </Typography>
          </Box>
        </Box>
        <Divider />
      </Box>

      <Box className="no-print" sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Card sx={{ flex: '1 1 calc(33.333% - 11px)', minWidth: 200 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h4" fontWeight="bold">{stats.totalItems}</Typography>
                <Typography variant="body2" color="text.secondary">{t.totalItems}</Typography>
              </Box>
              <InventoryIcon sx={{ fontSize: 50, opacity: 0.3, color: 'primary.main' }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 calc(33.333% - 11px)', minWidth: 200 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h4" fontWeight="bold">{formatNumber(stats.totalQuantity)}</Typography>
                <Typography variant="body2" color="text.secondary">{t.totalQuantity}</Typography>
              </Box>
              <TrendingUpIcon sx={{ fontSize: 50, opacity: 0.3, color: 'success.main' }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 calc(33.333% - 11px)', minWidth: 200 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h4" fontWeight="bold">{formatNumber(stats.totalValue)}</Typography>
                <Typography variant="body2" color="text.secondary">{t.totalValueSummary} ({t.kwd})</Typography>
              </Box>
              <MoneyIcon sx={{ fontSize: 50, opacity: 0.3, color: 'warning.main' }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Paper className="no-print" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Autocomplete
            size="small"
            options={[{ id: '' as number | '', label: t.allWarehouses }, ...warehouses.map(wh => ({ id: wh.warehouseId as number | '', label: isRTL ? wh.warehouseNameAr || wh.warehouseName : wh.warehouseName }))]}
            getOptionLabel={(o) => o.label || ''}
            value={[{ id: '' as number | '', label: t.allWarehouses }, ...warehouses.map(wh => ({ id: wh.warehouseId as number | '', label: isRTL ? wh.warehouseNameAr || wh.warehouseName : wh.warehouseName }))].find(o => o.id === warehouseFilter) || { id: '' as number | '', label: t.allWarehouses }}
            onChange={(_, v) => setWarehouseFilter(v?.id || '')}
            renderInput={(params) => <TextField {...params} label={t.warehouse} />}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            sx={{ minWidth: 200 }}
          />

          <Autocomplete
            size="small"
            options={[{ id: '' as number | '', label: t.allBranches }, ...branches.map(br => ({ id: br.branchId as number | '', label: isRTL ? br.branchNameAr || br.branchName : br.branchName }))]}
            getOptionLabel={(o) => o.label || ''}
            value={[{ id: '' as number | '', label: t.allBranches }, ...branches.map(br => ({ id: br.branchId as number | '', label: isRTL ? br.branchNameAr || br.branchName : br.branchName }))].find(o => o.id === branchFilter) || { id: '' as number | '', label: t.allBranches }}
            onChange={(_, v) => setBranchFilter(v?.id || '')}
            renderInput={(params) => <TextField {...params} label={t.branch} />}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            sx={{ minWidth: 200 }}
          />

          <Autocomplete
            size="small"
            options={[{ id: '' as number | '', label: t.allItems }, ...items.map(item => ({ id: item.itemId as number | '', label: `${item.itemCode} - ${isRTL ? item.itemNameAr || item.itemName : item.itemName}` }))]}
            getOptionLabel={(o) => o.label || ''}
            value={[{ id: '' as number | '', label: t.allItems }, ...items.map(item => ({ id: item.itemId as number | '', label: `${item.itemCode} - ${isRTL ? item.itemNameAr || item.itemName : item.itemName}` }))].find(o => o.id === itemFilter) || { id: '' as number | '', label: t.allItems }}
            onChange={(_, v) => setItemFilter(v?.id || '')}
            renderInput={(params) => <TextField {...params} label={t.item} />}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            sx={{ minWidth: 200 }}
          />

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.fromDate}
              value={fromDate ? new Date(fromDate) : null}
              onChange={(date) => {
                const val = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setFromDate(val);
                if (toDate && val && toDate < val) setToDate('');
              }}
              format="dd/MM/yyyy"
              slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } }, field: { clearable: true } }}
            />
          </LocalizationProvider>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.toDate}
              value={toDate ? new Date(toDate) : null}
              onChange={(date) => {
                const val = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setToDate(val);
                if (fromDate && val && fromDate > val) setFromDate('');
              }}
              format="dd/MM/yyyy"
              minDate={fromDate ? new Date(fromDate) : undefined}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } }, field: { clearable: true } }}
            />
          </LocalizationProvider>

          <FormControlLabel
            control={<Checkbox checked={showZeroQty} onChange={(e) => setShowZeroQty(e.target.checked)} />}
            label={t.showZeroQty}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button startIcon={<RefreshIcon />} onClick={loadData} variant="outlined">
            {t.refresh}
          </Button>
          <Button startIcon={<ExcelIcon />} onClick={exportToExcel} variant="contained" color="success">
            {t.exportExcel}
          </Button>
          <Button startIcon={<PdfIcon />} onClick={exportToPdf} variant="contained" color="error">
            {t.exportPdf}
          </Button>
          <Button startIcon={<PrintIcon />} onClick={handlePrint} variant="contained">
            {t.print}
          </Button>
          <Button startIcon={<ColumnsIcon />} onClick={(e) => setColumnsAnchor(e.currentTarget)} variant="outlined">
            {t.columns}
          </Button>
        </Box>
      </Paper>

      <Popover
        open={Boolean(columnsAnchor)}
        anchorEl={columnsAnchor}
        onClose={() => setColumnsAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            {t.columns}
          </Typography>
          <FormGroup>
            {Object.keys(visibleColumns).map((col) => (
              <FormControlLabel
                key={col}
                control={
                  <Checkbox
                    checked={visibleColumns[col as keyof typeof visibleColumns]}
                    onChange={() => handleColumnToggle(col as keyof typeof visibleColumns)}
                  />
                }
                label={t[col as keyof typeof t]}
              />
            ))}
          </FormGroup>
        </Box>
      </Popover>

      <TableContainer component={Paper} className="no-print" sx={{ mb: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, isDark ? 0.3 : 0.1) }}>
                {visibleColumns.itemCode && <TableCell><strong>{t.itemCode}</strong></TableCell>}
                {visibleColumns.itemName && <TableCell><strong>{t.itemName}</strong></TableCell>}
                {visibleColumns.unitName && <TableCell><strong>{t.unitName}</strong></TableCell>}
                {visibleColumns.location && <TableCell><strong>{t.location}</strong></TableCell>}
                {visibleColumns.quantity && <TableCell align="right"><strong>{t.quantity}</strong></TableCell>}
                {visibleColumns.reservedQty && <TableCell align="right"><strong>{t.reservedQty}</strong></TableCell>}
                {visibleColumns.availableQty && <TableCell align="right"><strong>{t.availableQty}</strong></TableCell>}
                {visibleColumns.unitCost && <TableCell align="right"><strong>{t.unitCost}</strong></TableCell>}
                {visibleColumns.totalValue && <TableCell align="right"><strong>{t.totalValue}</strong></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">{t.noData}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((item, index) => (
                  <TableRow key={index} hover>
                    {visibleColumns.itemCode && <TableCell>{item.itemCode}</TableCell>}
                    {visibleColumns.itemName && <TableCell>{isRTL ? item.itemNameAr : item.itemName}</TableCell>}
                    {visibleColumns.unitName && <TableCell>{isRTL ? item.unitNameAr : item.unitName}</TableCell>}
                    {visibleColumns.location && <TableCell>{getLocationName(item)}</TableCell>}
                    {visibleColumns.quantity && (
                      <TableCell align="right">
                        <Chip
                          label={formatNumber(item.quantity)}
                          size="small"
                          sx={{ bgcolor: alpha(getQuantityColor(item.quantity), 0.1), fontWeight: 'bold' }}
                        />
                      </TableCell>
                    )}
                    {visibleColumns.reservedQty && <TableCell align="right">{formatNumber(item.reservedQuantity)}</TableCell>}
                    {visibleColumns.availableQty && <TableCell align="right">{formatNumber(item.availableQuantity)}</TableCell>}
                    {visibleColumns.unitCost && <TableCell align="right">{fmtUnitPrice(item.unitCost)}</TableCell>}
                    {visibleColumns.totalValue && (
                      <TableCell align="right">
                        <Typography fontWeight="bold" color="primary">
                          {formatNumber(item.totalValue)}
                        </Typography>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
        <TablePagination
          component="div"
          count={totalRecords}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={t.rowsPerPage}
        />
      </TableContainer>

      <style>
        {`
          @media print {
            @page { 
              size: A4 landscape; 
              margin: 10mm;
            }
            body * { 
              visibility: hidden;
              font-family: Arial, 'Segoe UI', Tahoma, sans-serif !important;
            }
            .print-section, .print-section * { 
              visibility: visible;
              font-family: Arial, 'Segoe UI', Tahoma, sans-serif !important;
            }
            .print-section {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              background: white;
            }
            .no-print { display: none !important; }
            table {
              width: 100%;
              border-collapse: collapse;
              page-break-inside: auto;
              font-family: Arial, 'Segoe UI', Tahoma, sans-serif !important;
            }
            th, td {
              font-family: Arial, 'Segoe UI', Tahoma, sans-serif !important;
              font-size: 11pt !important;
              font-weight: 500 !important;
              color: #000 !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            th {
              font-weight: 700 !important;
            }
            tr { page-break-inside: avoid; page-break-after: auto; }
            thead { display: table-header-group; }
            tfoot { display: table-footer-group; }
          }
          @media screen {
            .print-section { display: none; }
          }
        `}
      </style>
    </Box>
  );
};

export default StockLevelsReport;